import { useQuery } from "@tanstack/react-query";
import {
  DEFAULT_WORKSPACE_TIMEZONE,
  setWorkspaceTimeZone,
} from "@/lib/utils";
import { useWorkspace } from "@/hooks/use-workspace";

interface WorkspaceTimezoneResponse {
  timezone: string;
}

/**
 * Reads the current workspace's configured *display* timezone and pushes it
 * into the shared module-level state in `@/lib/utils` so the pure date
 * formatters (`formatLocalDate`, `formatLocalDateTime`, ...) render in the
 * workspace zone without each call site threading a tz argument.
 *
 * Returns the resolved timezone string (defaulting to `America/Chicago` while
 * loading or when unset). Components that display timestamps can call this to
 * both keep the global in sync and re-render when the setting resolves.
 *
 * Implementation notes:
 * - The queryFn explicitly fetches `/api/workspace/timezone` (session-scoped
 *   endpoint). The workspaceSlug in the query key is a cache discriminator
 *   only — it is NOT appended to the URL. This avoids the default queryFn's
 *   `queryKey.join("/")` URL construction, which would produce the wrong path.
 * - On platform pages (outside WorkspaceProvider), useWorkspace() returns
 *   workspaceSlug "__no_workspace__". The fetch still runs; the server returns
 *   403 when there is no workspace permission context (pure platform sessions).
 *   The queryFn handles 403/401 by returning null rather than throwing, so
 *   those pages safely fall back to the default timezone.
 * - When a platform admin is impersonating a workspace, req.permissionContext
 *   IS set on the server, so the correct workspace timezone is returned.
 */
export function useWorkspaceTimezone(): string {
  const { workspaceSlug } = useWorkspace();

  const { data } = useQuery<WorkspaceTimezoneResponse | null>({
    // workspaceSlug is a cache key discriminator so workspace switches
    // invalidate the cached value — it is NOT appended to the fetch URL.
    queryKey: ["/api/workspace/timezone", workspaceSlug],
    queryFn: async () => {
      const res = await fetch("/api/workspace/timezone", {
        credentials: "include",
      });
      // Platform sessions (no workspace permission context) → server returns
      // 403. Treat gracefully: fall back to default rather than throwing.
      if (res.status === 401 || res.status === 403) return null;
      if (!res.ok) return null;
      return res.json() as Promise<WorkspaceTimezoneResponse>;
    },
    staleTime: 5 * 60 * 1000,
  });

  const timezone = data?.timezone || DEFAULT_WORKSPACE_TIMEZONE;

  // Set synchronously during render (not in an effect) so that when this hook
  // re-renders the app shell on resolve, the module-level value is already
  // updated before child pages render in the same pass — otherwise they'd show
  // the default zone for one extra render. Idempotent and StrictMode-safe.
  setWorkspaceTimeZone(timezone);

  return timezone;
}
